'use client';

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { GM_NESTED_PRODUCT_QUERY_IDENTIFIER, GM_PRODUCT_QUERY_IDENTIFIER } from '@/config/constants';
import { getSortedSizesOfGM } from '@/helpers/utils/productGroupMasters';
import { ProductSize, ProductStrapi } from '@/types/Product';

interface ProductContextProps {
  product?: ProductStrapi;
  sizes?: ProductSize[];
  selectedSize: string;
  nestedSizes?: ProductSize[];
  selectedNestedSize: string;
  onSizeChange: (key: string, isNestedSize?: boolean) => void;
}

interface ProductProviderProps {
  children: ReactNode;
  product?: ProductStrapi;
  gmSizes?: ProductSize[];
  nestedGmSizes?: ProductSize[];
  selectedGmProduct?: ProductStrapi;
  selectedNestedGmProduct?: ProductStrapi;
  selectedGmKey?: ProductSize['key'];
  selectedNestedGmKey?: ProductSize['key'];
  isShoppablePage?: boolean;
}

const ProductContext = createContext<ProductContextProps>({
  product: undefined,
  sizes: undefined,
  selectedSize: '',
  nestedSizes: undefined,
  selectedNestedSize: '',
  onSizeChange: () => {},
} as unknown as ProductContextProps);

const ProductProvider = ({
  product,
  children,
  gmSizes,
  nestedGmSizes,
  selectedGmKey,
  selectedGmProduct,
  selectedNestedGmKey,
  selectedNestedGmProduct,
  isShoppablePage = false,
}: ProductProviderProps) => {
  const currentGMItemIdentifier = GM_PRODUCT_QUERY_IDENTIFIER;
  const currentNestedGMItemIdentifier = GM_NESTED_PRODUCT_QUERY_IDENTIFIER;
  const searchParams = useSearchParams();
  const currentGMItem = searchParams.get(currentGMItemIdentifier);
  const currentNestedGMItem = searchParams.get(currentNestedGMItemIdentifier);
  const [strapiProduct, setStrapiProduct] = useState<ProductStrapi | undefined>(selectedGmProduct);
  const [sizes, setSizes] = useState<ProductSize[] | undefined>(gmSizes);
  const [nestedSizes, setNestedSizes] = useState<ProductSize[] | undefined>(nestedGmSizes);
  const [selectedSize, setSelectedSize] = useState(selectedGmKey || '');
  const [selectedNestedSize, setSelectedNestedSize] = useState(selectedNestedGmKey || '');
  const [finalStrapiProduct, setFinalStrapiProduct] = useState<ProductStrapi | undefined>(selectedNestedGmProduct);

  const strapiProductRef = useRef(selectedGmProduct);

  useEffect(() => {
    function updateProductSizes() {
      if (product?.attributes?.ProductGroupMembers?.data?.length) {
        const sortedSizes = getSortedSizesOfGM(product);

        setSizes(() => sortedSizes);

        if (sortedSizes?.length) {
          setStrapiProduct(() => sortedSizes[0].product);
          setSelectedSize(() => sortedSizes[0].key);
        }
      } else {
        setSizes(() => undefined);
        setSelectedSize(() => '');
      }
    }

    if (!gmSizes?.length) updateProductSizes();
  }, [product, gmSizes]);

  useEffect(() => {
    function updateCurrentSelectedProduct() {
      if (!currentGMItem) return;
      if (!product?.attributes?.ProductGroupMembers?.data?.length) return;
      const productToSelect = sizes?.find(
        size => size?.product?.attributes?.ItemCode == currentGMItem || size?.product?.attributes?.Slug == currentGMItem
      );

      if (!productToSelect?.product?.id) return;
      setStrapiProduct(productToSelect?.product);
      setSelectedSize(productToSelect?.key);
    }
    if (!selectedGmKey) updateCurrentSelectedProduct();
  }, [currentGMItem, product, sizes, selectedGmKey]);

  useEffect(() => {
    function updateCurrentSelectedProduct() {
      if (!currentNestedGMItem) return;
      if (!strapiProduct?.attributes?.ProductGroupMembers?.data?.length) return;
      const productToSelect = nestedSizes?.find(
        size =>
          size?.product?.attributes?.ItemCode == currentNestedGMItem ||
          size?.product?.attributes?.Slug == currentNestedGMItem
      );

      if (!productToSelect?.product?.id) return;
      setFinalStrapiProduct(productToSelect?.product);
      setSelectedNestedSize(productToSelect?.key);
    }
    if (!selectedNestedGmKey) updateCurrentSelectedProduct();
  }, [currentNestedGMItem, strapiProduct, nestedSizes, selectedNestedGmKey]);

  useEffect(() => {
    function updateProductNestedSizes() {
      if (strapiProduct?.attributes?.ProductGroupMembers?.data?.length) {
        const allSizes: ProductSize[] | undefined = getSortedSizesOfGM(strapiProduct, true);

        setNestedSizes(() => allSizes);

        if (allSizes?.length) {
          setFinalStrapiProduct(() => allSizes[0].product);
          setSelectedNestedSize(() => allSizes[0].key);
        }
      } else {
        setNestedSizes(() => undefined);
        setSelectedNestedSize(() => '');
      }

      strapiProductRef.current = strapiProduct;
    }

    if (!nestedGmSizes?.length || strapiProductRef?.current?.id !== strapiProduct?.id) updateProductNestedSizes();
  }, [strapiProduct, nestedGmSizes]);

  const handleSizeChange = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (key: string, isNestedSize?: boolean) => {
      if (isNestedSize) {
        const foundProductWithSize = nestedSizes?.find(s => s.key === key);

        console.log(foundProductWithSize, 'foundProductWithSize');

        if (foundProductWithSize) {
          setSelectedNestedSize(() => key);
          setFinalStrapiProduct(() => foundProductWithSize.product);
          const current = new URLSearchParams(
            Array.from(searchParams.entries())
              .filter(v => v[0] && v[0] !== currentGMItemIdentifier)
              .filter(v => v[0] && v[0] !== currentNestedGMItemIdentifier)
          );
          const queryParams = current.toString() ? `&${current.toString()}` : '';
          if (!isShoppablePage) {
            window.history.replaceState(
              null,
              '',
              `?${currentGMItemIdentifier}=${strapiProduct?.attributes?.ItemCode}&${currentNestedGMItemIdentifier}=${foundProductWithSize?.product?.attributes?.ItemCode}&${queryParams}`
            );
          }
        }
        return;
      }

      const foundProductWithSize = sizes?.find(s => s.key === key);
      console.log(foundProductWithSize, 'topfoundProductWithSize');
      if (foundProductWithSize) {
        setSelectedSize(() => key);
        setStrapiProduct(() => foundProductWithSize.product);

        if (nestedSizes?.length) {
          setSelectedNestedSize(() => nestedSizes[0].key);
          setFinalStrapiProduct(() => nestedSizes[0].product);
        }

        const current = new URLSearchParams(
          Array.from(searchParams.entries())
            .filter(v => v[0] && v[0] !== currentGMItemIdentifier)
            .filter(v => v[0] && v[0] !== currentNestedGMItemIdentifier)
        );
        const queryParams = current.toString() ? `&${current.toString()}` : '';
        console.log('isShoppablePage => ', isShoppablePage);
        if (!isShoppablePage) {
          window.history.replaceState(
            null,
            '',
            `?${currentGMItemIdentifier}=${foundProductWithSize?.product?.attributes?.ItemCode}&${queryParams}`
          );
        }
      }
    },
    [
      sizes,
      nestedSizes,
      searchParams,
      currentGMItemIdentifier,
      strapiProduct?.attributes?.ItemCode,
      currentNestedGMItemIdentifier,
    ]
  );

  const value = useMemo(
    () => ({
      product: finalStrapiProduct?.id && selectedNestedSize?.length ? finalStrapiProduct : strapiProduct,
      sizes,
      selectedSize,
      nestedSizes,
      selectedNestedSize,
      onSizeChange: handleSizeChange,
    }),
    [finalStrapiProduct, strapiProduct, sizes, selectedSize, handleSizeChange, nestedSizes, selectedNestedSize]
  );
  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

export const useProductContext = () => {
  return useContext(ProductContext);
};

export default ProductProvider;
